/********************************************
 * File: src/App.js
 ********************************************/
import React, { useState, useEffect, useRef } from 'react';
import { Shield, TrendingUp, Building2, Globe } from 'lucide-react';

// 1) Put steps outside the component or define them via memo
const STEPS = ["ASI", "ASInalyse", "Asinalyse"];

const AnimatedTitle = () => {
  const [displayText, setDisplayText] = useState(STEPS[0]);
  const stepInterval = 1200; // time in ms for each step
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        const next = prev + 1;
        if (next >= STEPS.length) return 0; // loop back
        return next;
      });
    }, stepInterval);

    return () => clearInterval(interval);
  }, [stepInterval]);

  useEffect(() => {
    setDisplayText(STEPS[currentStep]);
  }, [currentStep]);

  return (
    <h1 className="text-3xl font-bold transition-all duration-300 ease-in-out">
      {displayText}
    </h1>
  );
};

const BinaryRainBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    const fontSize = 10;
    const columns = canvas.width / fontSize;
    const drops = Array(Math.floor(columns)).fill(1);
    const chars = ['0', '1'];

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;

      drops.forEach((drop, i) => {
        const char = chars[Math.floor(Math.random() * chars.length)];

        if (char === '1') {
          const blueIntensity = Math.floor(Math.random() * 55 + 200);
          ctx.fillStyle = `rgba(${blueIntensity}, ${blueIntensity + 20}, 255, 0.4)`;
        } else {
          const blueIntensity = Math.floor(Math.random() * 55 + 100);
          ctx.fillStyle = `rgba(${blueIntensity}, ${blueIntensity + 20}, 255, 0.15)`;
        }

        ctx.fillText(char, i * fontSize, drop * fontSize);

        // Reset drop to top after random length
        if (drop * fontSize > canvas.height && Math.random() > (char === '1' ? 0.98 : 0.96)) {
          drops[i] = 0;
        }
        drops[i] += char === '1' ? 1.2 : 0.8;
      });
    };

    const interval = setInterval(draw, 50);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none opacity-30"
    />
  );
};

const Website = () => {
  const [activeSection, setActiveSection] = useState('home');

  const sections = {
    home: {
      icon: <Building2 className="w-6 h-6" />,
      title: "Home",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">Artificial Super Intelligence analytics</h2>
          <p className="text-lg">
            Asinalyse is a FinTech company leveraging Artificial Super Intelligence to analyze data and enable data-driven investing and decision-making. 
            As a company registered in Estonia, we prioritize transparency and efficiency in all operations. 
            Our sophisticated AI algorithms combine with cutting-edge research methodologies to identify market patterns and predict future movements before they emerge. 
            This enables precise, strategic execution at optimal moments.
          </p>
        </div>
      )
    },
    fintech: {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "FinTech",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">FinTech Innovation</h2>
          <p className="text-lg">
            At the forefront of financial innovation, we combine:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-lg">Machine learning for deep financial insights</li>
            <li className="text-lg">High-performance computing for real-time data processing</li>
            <li className="text-lg">Advanced AI-driven analytics for strategic decision-making</li>
          </ul>
        </div>
      )
    },
    security: {
      icon: <Shield className="w-6 h-6" />,
      title: "Security",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">Compliance and Security</h2>
          <p className="text-lg">
            Asinalyse operates under strict supervision of EU financial regulations. 
            We prioritize:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-lg">Full compliance with EU financial directives</li>
            <li className="text-lg">Robust cybersecurity and data protection</li>
            <li className="text-lg">Transparent reporting and documentation</li>
          </ul>
        </div>
      )
    },
    contact: {
      icon: <Globe className="w-6 h-6" />,
      title: "Contact",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
          <p className="text-lg">Asinalyse</p>
          <p className="text-lg">Tallinn, Estonia</p>
          <p className="text-lg">Email: contact@asinalyse.com</p>
        </div>
      )
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden">
      <BinaryRainBackground />

      {/* Header with animated title */}
      <header className="relative bg-black bg-opacity-40 backdrop-blur-lg fixed w-full z-10">
        <div className="container mx-auto px-4 py-4 flex justify-center">
          <AnimatedTitle />
        </div>
      </header>

      <main className="relative container mx-auto px-4 pt-24 pb-32 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-black bg-opacity-50 backdrop-blur-lg rounded-xl p-8">
            {sections[activeSection].content}
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 w-full bg-black bg-opacity-40 backdrop-blur-lg z-10">
        <div className="container mx-auto">
          <div className="flex justify-around py-4">
            {Object.entries(sections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`flex flex-col items-center space-y-2 p-2 rounded-lg transition-colors
                  ${activeSection === key 
                    ? 'bg-blue-600 text-white' 
                    : 'hover:bg-blue-600 hover:bg-opacity-50'}`}
              >
                {section.icon}
                <span className="text-sm">{section.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Website;
